import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { getNav } from "@/lib/navConfig";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { StatusBadge, UrgencyBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Loader2, PlusCircle, Building2, MapPin, ClipboardList } from "lucide-react";

export default function TenantHome() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [property, setProperty] = useState(null);
  const [requests, setRequests] = useState(null);

  useEffect(() => {
    api.get("/tenant/property").then(({ data }) => setProperty(data)).catch(() => setProperty(false));
    api.get("/tenant/work-orders").then(({ data }) => setRequests(data)).catch(() => setRequests([]));
  }, []);

  return (
    <DashboardLayout nav={getNav("tenant")} title="Maintenance Portal">
      {property && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6 flex items-center justify-between flex-wrap gap-3" data-testid="tenant-property-card">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg bg-blue-50 flex items-center justify-center"><Building2 className="w-6 h-6 text-blue-600" /></div>
            <div>
              <h2 className="font-head text-lg font-semibold text-slate-900">{property.name}</h2>
              <p className="text-sm text-slate-500 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {property.street_address}{property.unit_number ? `, #${property.unit_number}` : ""}, {property.city}, {property.state}</p>
            </div>
          </div>
          <Button data-testid="tenant-new-request-btn" onClick={() => navigate("/app/tenant/new")} className="bg-blue-600 hover:bg-blue-700"><PlusCircle className="w-4 h-4 mr-1" /> New Request</Button>
        </div>
      )}

      <h3 className="font-head font-semibold text-slate-900 mb-3 flex items-center gap-2"><ClipboardList className="w-4 h-4" /> My Requests</h3>
      {!requests ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div> :
        requests.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
            <p className="text-slate-400 text-sm">No maintenance requests yet.</p>
            <Button data-testid="tenant-empty-new-btn" onClick={() => navigate("/app/tenant/new")} className="mt-4 bg-blue-600 hover:bg-blue-700"><PlusCircle className="w-4 h-4 mr-1" /> Submit a Request</Button>
          </div>
        ) : (
          <div className="space-y-2">
            {requests.map((w) => (
              <button key={w.id} data-testid={`tenant-req-${w.id}`} onClick={() => navigate(`/app/tenant/requests/${w.id}`)} className="w-full text-left flex items-center justify-between bg-white border border-slate-200 rounded-xl p-4 hover:border-blue-300 transition-colors">
                <div>
                  <div className="flex items-center gap-2"><span className="font-medium text-slate-900">{w.wo_number}</span><UrgencyBadge urgency={w.urgency} /></div>
                  <div className="text-sm text-slate-500 mt-0.5">{w.service_category} — {w.problem_description}</div>
                </div>
                <StatusBadge status={w.status} />
              </button>
            ))}
          </div>
        )}
    </DashboardLayout>
  );
}
